/**
 * Every analytics read Home's dashboard shows, over the range Home's picker chose: the KPI tiles
 * (watch time, plays, viewers and the vertical's headline, with the previous period's daily series
 * for the trend chart's faint line), what is being watched right now (refreshed every minute while
 * the tab is visible), the most played items and, for a brand that sells, money; for a brand that
 * does not, the devices people watch on instead. One place, so Home's single "Sample data" chip
 * knows whether any of it is sample.
 */

import { homeKpiTiles } from "../definitions";
import { analytics, type AudienceReport, type ContentReport, type MoneyReport, type Realtime } from "../api";
import type { ReportScope } from "../scope";
import { useKpis, type Kpis } from "../use-kpis";
import { useReport, type Report } from "../use-report";

export interface HomeAnalytics {
  kpis: Kpis;
  realtime: Report<Realtime>;
  content: Report<ContentReport>;
  /** This period and the one before; null data unless the brand sells. */
  money: Report<{ now: MoneyReport; before: MoneyReport } | null>;
  /** The devices people watch on; null data unless the brand is known not to sell. */
  audience: Report<AudienceReport | null>;
  sample: boolean;
}

const REALTIME_EVERY = 60_000;

/** `sells` is null while the brand's products are still loading: neither money nor devices is read yet. */
export function useHomeAnalytics(scope: ReportScope, sells: boolean | null): HomeAnalytics {
  const { client, range, previous } = scope;
  const kpis = useKpis(scope, { ids: homeKpiTiles(scope.pack), previousSeries: true });
  const realtime = useReport(() => analytics.realtime(client), [client], { refreshMs: REALTIME_EVERY });
  const content = useReport(() => analytics.content(client, range), [client, range]);
  const money = useReport(
    () => (sells ? Promise.all([analytics.money(client, range), analytics.money(client, previous)]).then(([now, before]) => ({ now, before })) : Promise.resolve(null)),
    [client, range, previous, sells],
  );
  const audience = useReport(() => (sells === false ? analytics.audience(client, range) : Promise.resolve(null)), [client, range, sells]);
  const sample = kpis.sample || !!realtime.data?.sample || !!content.data?.sample || !!money.data?.now.sample || !!audience.data?.sample;
  return { kpis, realtime, content, money, audience, sample };
}
